// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import { inject, Injectable } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NotificationService } from './notification-service';
import {Store} from '@ngrx/store';
import {AppState, logout} from '../app/store/app.state';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import {IDecodedAuthToken} from '../types/auth';
import { MatDialog } from '@angular/material/dialog';

@Injectable({providedIn: 'root'})
export class AuthService {
    // Injected dependencies first
    public readonly jwtHelper = inject(JwtHelperService);
    private readonly router = inject(Router);
    private readonly notificationService = inject(NotificationService);
    private readonly store = inject(Store<AppState>);
    private readonly translate = inject(TranslateService);
    private readonly dialog = inject(MatDialog);

    // Initialize subscription tracking before authentication
    private tokenCheck: Subscription | null = null;

    public showNav: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public showToolbar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public showBanners: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private isLoggedInSubject = new BehaviorSubject<boolean>(false);
    public maintenanceMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly token$: BehaviorSubject<string | null> = new BehaviorSubject(localStorage.getItem('token'));
    public readonly mapToken$: BehaviorSubject<string | null> = new BehaviorSubject(localStorage.getItem('mapToken'));

    constructor() {
        this.isLoggedInSubject.next(this.isAuthenticated());
    }

    get isLoggedIn$(): Observable<boolean> {
        return this.isLoggedInSubject.asObservable();
    }

    get isLoggedIn(): boolean {
        return this.isLoggedInSubject.value;
    }

    public login(token: string, mapToken: string, rememberMe: boolean, activationToken: string, destination: string = null) {
        let expired: boolean;

        try {
            expired = this.jwtHelper.isTokenExpired(token);
        } catch (e) {
            this.notificationService.showError(this.translate.instant('login.error-unknown'));
            Sentry.captureException(e);
            return this.logout_with_route(null, expired);
        }

        if (expired) {
          Sentry.captureMessage("A login failed because of a system clock error.");
          this.notificationService.showError(this.translate.instant('login.error-system-clock'));
          return this.logout_with_route(null, expired);
        }

        if (rememberMe) {
            localStorage.setItem('token', token);
            localStorage.setItem('mapToken', mapToken);
            this.token$.next(token);
            this.mapToken$.next(mapToken);
        }

        this.isLoggedInSubject.next(true);
        this.startTokenExpirationChecker();

        if (activationToken) {
            // TODO: This used to go to the licenses page, to display the activation token and allow you to activate, does it still apply?
            this.router.navigate(['/']);
        } else {
            if (destination != null && destination.length > 0) {
                this.router.navigateByUrl(destination);
            } else {
                this.router.navigate(['/']);
            }
        }
    }

    public logout(expiring = false) {
        this.showBanners.next(false);
        this.notificationService.dismiss();
        return this.logout_with_route(null, expiring);
    }

    startTokenExpirationChecker() {
        if (this.tokenCheck) {
            return;
        }
        this.tokenCheck = interval(15000).subscribe(() => {
            const auth_token = localStorage.getItem('token');
                let expired;
                try {
                    expired = this.jwtHelper.isTokenExpired(auth_token);
                } catch (e) {
                    Sentry.captureException(e);
                    expired = true;
                }
            if (auth_token && expired) {
                this.logout(true);
            }
        });
    }

    stopTokenExpirationChecker() {
        if (this.tokenCheck) {
            this.tokenCheck.unsubscribe();
            this.tokenCheck = null;
        }
    }

    public logoutWithInvitation(route) {
        this.store.dispatch(logout());
        this.dialog.closeAll();
        this.isLoggedInSubject.next(false);
        this.clearLocalStorage();

        if (route.url.length === 2) {
            const invitationPath = route.url[0].path;
            if (invitationPath === 'invitation' ||
                invitationPath === 'd3e-invitation' ||
                invitationPath === 'cigent-invitation' ||
                invitationPath === 'verify-free') {
                // this.navigate(browserWindow).toUrl(`/${invitationPath}/${route.url[1].path}`);
                this.router.navigate([`${invitationPath}`, `${route.url[1].path}`]);
            }
        }

    }

    public logout_with_route(route, expiring = false) {
        this.store.dispatch(logout());
        this.dialog.closeAll();
        this.clearLocalStorage();
        this.isLoggedInSubject.next(false);

        this.stopTokenExpirationChecker();
        if (expiring) {
            this.router.navigate(['login'], {
                queryParams: {
                    expiring: true
                }
            });
        } else {
            if (!this.router.url.startsWith('/login') &&
                !this.router.url.startsWith('/subscriptions/plustrial') &&
                !this.router.url.startsWith('/okta/callback') &&
                !this.router.url.startsWith('/reset')) {

                const encodedDestUrl = this.encode_destination_url(route, '/login');
                this.router.navigate([encodedDestUrl]);
                // this.navigate(browserWindow).toUrl(encodedDestUrl);
            }
        }
    }

    public getToken(): string {
        return 'Bearer ' + localStorage.getItem('token');
    }

    public hasToken(): boolean {
        return !!localStorage.getItem('token');
    }

    public isAuthenticated(): boolean {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                this.startTokenExpirationChecker();
            }
            // Check whether the token is expired and return
            // true or false
            if (token) {
                const expired = this.jwtHelper.isTokenExpired(token as string);
                return !expired;
            }
            return false;

        } catch (error) {
            Sentry.captureException(error);
            this.store.dispatch(logout())
            localStorage.removeItem('token');
            return false;
        }
    }

    private clearLocalStorage() {
        localStorage.removeItem('token');
        localStorage.removeItem('downloadId');
        localStorage.removeItem('domain');
        localStorage.removeItem('fb_uid');
        localStorage.removeItem('okta-client-id');
        localStorage.removeItem('okta-issuer');
    }

    public setSentryTagsFromToken() {
        let decoded: IDecodedAuthToken;
        try {
            decoded = this.jwtHelper.decodeToken<IDecodedAuthToken>(localStorage.getItem('token'));
        } catch {
            decoded = null;
        }
        if (!!decoded?.token_id) {
            Sentry.setTag('auth.token_id', decoded?.token_id);
        }
    }

    private encode_destination_url(destinationRoute, baseUrl: string) {
        if (destinationRoute != null) {
            let encodedUri: string = null;
            const destinationUrl = destinationRoute.url;
            if (destinationUrl !== undefined && destinationUrl != null && destinationUrl.length > 0) {
                let path = '';
                const slash = '/';
                destinationUrl.forEach((urlSegment: UrlSegment) => {
                    path += slash;
                    path += urlSegment.path;
                });

                // %2F ->
                encodedUri = '?dest=' + encodeURIComponent(path);

                let i = 0;
                for (const queryParam in destinationRoute.queryParams) {
                    if (destinationRoute.queryParams.hasOwnProperty(queryParam)) {
                        if (i === 0) {
                            encodedUri += encodeURIComponent('?');
                        } else {
                            // only put this of there are more than one
                            encodedUri += encodeURIComponent('&');
                        }
                        // the router will decode all the uris, so encode them again.
                        const paramValue = encodeURIComponent(destinationRoute.queryParams[queryParam]);
                        const parameter = encodeURIComponent(queryParam + '=' + paramValue);
                        encodedUri += parameter;
                        i++;
                    }
                }
                baseUrl += encodedUri;
            }
        }
        return baseUrl;
    }

}
